import type { RestStore } from "@KassaroAB/shared/types/store";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import { Card } from "../components/Card";
import styled from "styled-components";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { useTranslation } from "react-i18next";
import { LanguageToggle } from "../components/LanguageToggle";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: ${spacing.x3}px;
  background-color: ${colors.backgroundLight};
  position: relative;
`;

const LangCorner = styled.div`
  position: absolute;
  top: ${spacing.x2}px;
  right: ${spacing.x2}px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${colors.text};
  margin-bottom: ${spacing.x1}px;
`;

const Subtitle = styled.p`
  font-size: ${fontSizes.regular}px;
  color: ${colors.textMuted};
  margin-bottom: ${spacing.x4}px;
`;

const StoreList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x3}px;
  width: 100%;
  max-width: 600px;
`;

const StoreCard = styled(Card)`
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const StoreName = styled.h2`
  font-size: ${fontSizes.large}px;
  font-weight: 600;
  color: ${colors.text};
  margin: 0;
`;

interface StoreSelectorProps {
  stores: RestStore[];
  onSelectStore: (storeId: StoreId) => void;
}

export const StoreSelector = ({ stores, onSelectStore }: StoreSelectorProps) => {
  const { t } = useTranslation();
  return (
    <Container>
      <LangCorner>
        <LanguageToggle />
      </LangCorner>
      <Title>{t("storeSelector.title")}</Title>
      <Subtitle>{t("storeSelector.subtitle")}</Subtitle>
      <StoreList>
        {stores.map((store) => (
          <StoreCard key={store.id} onClick={() => onSelectStore(store.id)}>
            <StoreName>{store.name}</StoreName>
          </StoreCard>
        ))}
      </StoreList>
    </Container>
  );
};

